import { BookHeart, Clock, Sparkles, type LucideIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { AppRoutes } from "@/shared/lib/routes"
import { LegalLink } from "@/shared/components/legal-link"
import type { StepComponentProps } from "./step-contract"

/** lucide keys usable for cover feature chips. Extend as new covers need icons. */
const ICONS: Record<string, LucideIcon> = {
  clock: Clock,
  sparkles: Sparkles,
  "book-heart": BookHeart,
}

/**
 * Dumb full-bleed cover/intro screen: a full-width hero (edge-to-edge, not boxed
 * by the content padding), feature chips, title, CTA and legal links. All content
 * comes from the step config; the CTA just calls `onAdvance`.
 */
export function CoverStep({ step, onAdvance }: StepComponentProps) {
  return (
    <div data-slot="cover-step" className="flex flex-1 flex-col bg-dark-base pb-8 text-purple-light">
      {step.coverImage && (
        <img src={step.coverImage} alt="" className="w-full object-cover" />
      )}

      <div className="flex flex-1 flex-col px-4">
        <div className="mt-4 space-y-2 text-center">
          <h1 className="font-display text-3xl font-medium text-purple-light">{step.title}</h1>
          {step.subtitle && <p className="text-base text-purple-light/70">{step.subtitle}</p>}
        </div>

        {step.features && step.features.length > 0 && (
          <div className="mt-6 flex justify-center gap-3">
            {step.features.map((feature) => {
              const Icon = ICONS[feature.icon]
              return (
                <div
                  key={feature.label}
                  className="flex flex-1 flex-col items-center gap-2 rounded-2xl bg-purple-light/10 px-3 py-4 text-center"
                >
                  {Icon && <Icon className="size-6 text-purple-light" />}
                  <span className="text-xs font-medium text-purple-light">{feature.label}</span>
                </div>
              )
            })}
          </div>
        )}

        <div className="mt-auto space-y-4 pt-6">
          <Button variant="gradient" size="xl" onClick={onAdvance}>
            {step.cta ?? "Continue"}
          </Button>
          <div className="flex justify-center gap-4 text-xs text-purple-light/50">
            <LegalLink to={AppRoutes.PRIVACY}>Privacy Policy</LegalLink>
            <LegalLink to={AppRoutes.TERMS}>Terms of use</LegalLink>
            <LegalLink to={AppRoutes.COOKIES}>Cookie Policy</LegalLink>
          </div>
        </div>
      </div>
    </div>
  )
}
